/**
 * Email Gate Page
 * First step: verifies the user's email is in ADMIN_EMAILS
 */

import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Container, Typography, Button } from '@mui/material';
import { useAdminAuthStore } from '@/stores/adminAuthStore';
import { platformAdminApi } from '@/api/client';

export function EmailGatePage() {
  const { user, getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();
  const setEmailVerified = useAdminAuthStore((s) => s.setEmailVerified);
  const [status, setStatus] = useState<'checking' | 'denied' | 'error'>('checking');

  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        // Call verify-email with email from user object (from ID token)
        const result = await platformAdminApi.verifyAdminEmail(token, user?.email || '');

        if (result.allowed) {
          setEmailVerified(true);
          navigate('/token-gate');
        } else {
          setStatus('denied');
        }
      } catch (err) {
        console.error('[EmailGatePage] Verification error:', err);
        setStatus('error');
      }
    })();
  }, [user?.email, getAccessTokenSilently, setEmailVerified, navigate]);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  if (status === 'checking') {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography>Verifying your email...</Typography>
        </Box>
      </Container>
    );
  }

  if (status === 'denied') {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5">Access Denied</Typography>
          <Typography variant="body2" color="textSecondary">
            Your email ({user?.email}) is not authorized to access the admin panel.
          </Typography>
          <Button variant="contained" onClick={handleLogout}>
            Logout
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="h5">Error</Typography>
        <Typography variant="body2" color="textSecondary">
          An error occurred during verification. Please try logging in again.
        </Typography>
        <Button variant="contained" onClick={handleLogout}>
          Logout
        </Button>
      </Box>
    </Container>
  );
}
